import { observer } from "mobx-react-lite";
import { AlertTriangle } from "lucide-react";
import { useTran } from "@/hooks";
import { dashboardEChartsStore } from "../../../stores/e-charts.store";
import { dashboardEChartsTotalsStore } from "../../../stores/e-charts-totals.store";
import { otherDimension } from "../../../utils/e-charts-dimensions";
import {
  buildUnknownWithinWarningCopy,
  unknownWithinEntityIds,
} from "../../../utils/e-charts-view";
import { subtreeIds } from "../../../utils/hierarchy-picker";

const findBuAncestorOverlaps = (tree, ids, nameById) => {
  if (!Array.isArray(ids) || ids.length < 2 || !tree?.length) return [];

  const subtreeOf = new Map();
  for (const id of ids) {
    subtreeOf.set(String(id), new Set(subtreeIds(tree, String(id))));
  }

  const out = [];
  for (const a of ids) {
    for (const b of ids) {
      if (a === b) continue;
      if (subtreeOf.get(String(a))?.has(String(b))) {
        out.push({
          ancestor: nameById?.get(String(a)) ?? String(a),
          descendant: nameById?.get(String(b)) ?? String(b),
        });
      }
    }
  }

  return out;
};

const formatWarning = (warning, t) => {
  switch (warning.code) {
    case "UNSUPPORTED_FRAMEWORK":
      return t(
        `Scope breakdown isn't supported for ${warning.framework} - values shown as "-".`,
      );
    case "UNKNOWN_FRAMEWORK":
      return t(
        `Framework ${warning.framework} is not configured for this tenant.`,
      );
    case "MULTIPLE_FRAMEWORKS":
      return t(
        `Multiple frameworks active - values shown from ${warning.chosen} to avoid double-counting.`,
      );
    case "UNMAPPED_SCOPE":
      return t(
        `No disclosure found for ${warning.scope} - values shown as "-".`,
      );
    case "EMPTY_SCOPE":
      return t("No data available within the current filter scope.");
    case "UNKNOWN_ENTITY":
      return null;
    // Handled separately below: it needs the within dimension's display names
    // and is suppressed when an empty state already explains it.
    case "UNKNOWN_WITHIN_ENTITY":
      return null;
    default:
      return null;
  }
};

const EChartsWarnings = observer(
  ({
    orgTree,
    // From the panel, not the store: warnings describe the stored rows, so
    // while a refetch is pending the dimension naming must follow the view
    // the rows answer, not the ambient state that already moved on.
    displayedDimension,
    entityNameById,
    effectiveEntities = [],
    withinNameById,
    suppressUnknownWithin = false,
    // The chart-derived lines describe the stored (outgoing) response, so
    // while that view is veiled the banner dims with it: full-strength text
    // would read as a claim about the NEW filters (this build's Codex
    // finding 3). It swaps atomically with the data like everything else.
    dimmed = false,
  } = {}) => {
    const { t } = useTran();
    const store = dashboardEChartsStore;
    const allWarnings = [
      ...store.warnings,
      ...dashboardEChartsTotalsStore.warnings,
      // Strip-fetch warnings are per-year; show only the displayed year's.
      ...dashboardEChartsTotalsStore.extraWarningsFor(store.selectedYear),
    ];

    const messages = [];
    let mixedUnitCount = 0;

    for (const warning of allWarnings) {
      if (warning.code === "MIXED_UNIT") {
        mixedUnitCount += 1;
        continue;
      }

      const message = formatWarning(warning, t);
      if (message && !messages.includes(message)) {
        messages.push(message);
      }
    }

    // Partial unknown-within: some cross-filter selections resolved, others did
    // not, and the chart is still drawing. Without this line the user silently
    // sees fewer entities than they picked. Suppressed only while an empty
    // state is rendering its own explanation for the same warning (item 13).
    if (!suppressUnknownWithin) {
      const unknownWithinCopy = buildUnknownWithinWarningCopy({
        withinDimension: otherDimension(displayedDimension),
        unknownIds: unknownWithinEntityIds(store.warnings),
        nameById: withinNameById,
      });
      if (unknownWithinCopy) {
        const message = t(unknownWithinCopy.key, unknownWithinCopy.params);
        if (!messages.includes(message)) messages.push(message);
      }
    }

    if (mixedUnitCount > 0) {
      messages.push(
        t(
          `${mixedUnitCount} cell${mixedUnitCount === 1 ? "" : "s"} have unit mismatches - values excluded.`,
        ),
      );
    }

    if (store.consolidated && displayedDimension === "bu") {
      const overlaps = findBuAncestorOverlaps(
        orgTree,
        effectiveEntities,
        entityNameById,
      );

      for (const { ancestor, descendant } of overlaps) {
        const message = t(
          `${descendant} sits inside ${ancestor} - consolidating both will double-count ${descendant}'s emissions.`,
        );
        if (!messages.includes(message)) {
          messages.push(message);
        }
      }
    }

    if (messages.length === 0) return null;

    return (
      <div
        className={`flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700 transition-opacity dark:text-amber-400 ${
          dimmed ? "opacity-60" : ""
        }`}
      >
        <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
        <ul className="space-y-0.5">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      </div>
    );
  },
);

export default EChartsWarnings;
